// Sprint 4.3 Phase C — Immutable Warehouse Movement Ledger viewer
// Read-only audit log: every WH-side stock / asset movement is appended
// here by PI / IV / RV / WT / Asset Repair posters. Cannot be edited
// or deleted from the UI.
import { useEffect, useMemo, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/ui-helpers";
import { BranchFilter } from "@/components/BranchFilter";
import { Search, Layers, ScrollText } from "lucide-react";
import { useClientPagination } from "@/hooks/useClientPagination";
import { PaginationControls } from "@/components/PaginationControls";
import UserLabel, { formatUser, useUserDirectory } from "@/components/UserLabel";

const MOVEMENT_TYPES = [
  ["", "All movements"],
  ["purchase_receipt", "Purchase Receipt"],
  ["inventory_issue", "Inventory Issue"],
  ["asset_issue", "Asset Issue"],
  ["inventory_return", "Inventory Return"],
  ["asset_return", "Asset Return"],
  ["warehouse_transfer", "Warehouse Transfer"],
  ["asset_repair_sent", "Repair Sent"],
  ["asset_repair_return", "Repair Return"],
];

const TYPE_TONE = {
  purchase_receipt:    "bg-emerald-50 text-emerald-800 border-emerald-200",
  inventory_issue:     "bg-orange-50 text-orange-800 border-orange-200",
  asset_issue:         "bg-orange-50 text-orange-800 border-orange-200",
  inventory_return:    "bg-blue-50 text-blue-800 border-blue-200",
  asset_return:        "bg-blue-50 text-blue-800 border-blue-200",
  warehouse_transfer:  "bg-violet-50 text-violet-800 border-violet-200",
  asset_repair_sent:   "bg-amber-50 text-amber-900 border-amber-200",
  asset_repair_return: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

export default function WarehouseMovementsPage() {
  useUserDirectory();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterBranch, setFilterBranch] = useState("");
  const [filterWarehouse, setFilterWarehouse] = useState("");
  const [filterType, setFilterType] = useState("");
  const [search, setSearch] = useState("");
  const [warehouses, setWarehouses] = useState([]);

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterBranch) params.append("branch_id", filterBranch);
      if (filterWarehouse) params.append("warehouse_id", filterWarehouse);
      if (filterType) params.append("movement_type", filterType);
      params.append("limit", "500");
      const r = await api.get(`/warehouse-movements?${params}`);
      setRows(Array.isArray(r.data) ? r.data : (r.data?.items || []));
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterBranch, filterWarehouse, filterType]);

  // Warehouse dropdown — reloads when branch changes (respects user access)
  useEffect(() => {
    const url = filterBranch
      ? `/warehouses/picker?branch_id=${filterBranch}`
      : `/warehouses/picker`;
    api.get(url).then(r => setWarehouses(Array.isArray(r.data) ? r.data : []))
       .catch(() => setWarehouses([]));
    // Reset warehouse filter when branch changes if it's outside scope
    if (filterWarehouse) setFilterWarehouse("");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBranch]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => [
      r.movement_no, r.reference_no, r.item_name, r.asset_code,
      r.from_warehouse_name, r.to_warehouse_name, r.branch_name,
      r.movement_type, r.reference_type, r.created_by,
      // Also search on the resolved user name.
      formatUser(r.created_by, ""),
    ].some(x => (x || "").toString().toLowerCase().includes(q)));
  }, [rows, search]);

  const { paged, page, pageSize, total, setPage, setPageSize } = useClientPagination(filtered, { initialPageSize: 50 });

  return (
    <div>
      <PageHeader
        eyebrow="Stores · Audit"
        title="Warehouse Movement Ledger"
        subtitle="Immutable audit log — every PI, Issue, Return, Transfer & Repair touch on a warehouse."
      />

      <div className="mb-3 flex items-center gap-3 flex-wrap">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="whm-search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search movement / item / asset / reference…"
            className="border border-slate-300 bg-white pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-[#F97316] min-w-[300px]" />
        </div>
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Branch</span>
        <BranchFilter value={filterBranch} onChange={setFilterBranch} testid="whm-branch-filter" />
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Warehouse</span>
        <select data-testid="whm-warehouse-filter" value={filterWarehouse}
          onChange={e => setFilterWarehouse(e.target.value)}
          className="border border-slate-300 bg-white px-3 py-2 text-sm">
          <option value="">All warehouses</option>
          {warehouses.map(w => (
            <option key={w.id} value={w.id}>{w.warehouse_code} — {w.warehouse_name}</option>
          ))}
        </select>
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Type</span>
        <select data-testid="whm-type-filter" value={filterType}
          onChange={e => setFilterType(e.target.value)}
          className="border border-slate-300 bg-white px-3 py-2 text-sm">
          {MOVEMENT_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <div className="ml-auto text-xs text-slate-500">
          <span className="text-slate-400 mr-1">Showing:</span>
          <span className="font-bold text-slate-800 tabular">{filtered.length}</span>
          <span className="text-slate-400 mx-1">/</span>
          <span className="text-slate-600 tabular">{rows.length}</span>
        </div>
      </div>

      {loading ? (
        <EmptyState title="Loading…" />
      ) : rows.length === 0 ? (
        <EmptyState title="No warehouse movements yet" icon={ScrollText}
          hint="Approve a PI / Issue / Return / Transfer to populate this ledger." />
      ) : filtered.length === 0 ? (
        <EmptyState title="No matches" />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="whm-table">
              <thead className="bg-slate-50 border-b-2 border-slate-200">
                <tr>{["Movement No", "Date", "Type", "Branch", "From WH", "To WH", "Item / Asset", "Qty", "Reference", "Created By"].map(h =>
                  <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">{h}</th>
                )}</tr>
              </thead>
              <tbody>
                {paged.map(m => (
                  <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`whm-row-${m.id}`}>
                    <td className="py-3 px-4 text-xs font-mono font-bold text-slate-700">{m.movement_no || m.id?.slice(0, 8)}</td>
                    <td className="py-3 px-4 text-xs text-slate-600">{(m.created_at || "").slice(0, 10)}</td>
                    <td className="py-3 px-4">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${TYPE_TONE[m.movement_type] || "bg-slate-50 text-slate-700 border-slate-200"}`}>
                        {(MOVEMENT_TYPES.find(([v]) => v === m.movement_type) || [m.movement_type, m.movement_type])[1]}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-600">{m.branch_name || "—"}</td>
                    <td className="py-3 px-4 text-xs text-slate-700">{m.from_warehouse_name || "—"}</td>
                    <td className="py-3 px-4 text-xs text-slate-700 font-bold">{m.to_warehouse_name || "—"}</td>
                    <td className="py-3 px-4 text-sm text-slate-800">
                      {m.line_kind === "Asset"
                        ? <><span className="font-mono text-xs font-bold">{m.asset_code}</span></>
                        : <>{m.item_name}</>}
                    </td>
                    <td className="py-3 px-4 text-sm tabular">{m.quantity} {m.unit || ""}</td>
                    <td className="py-3 px-4 text-xs">
                      <div className="text-slate-700 font-mono">{m.reference_no || "—"}</div>
                      <div className="text-[10px] text-slate-400 normal-case">{m.reference_type || "—"}</div>
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-500"><UserLabel value={m.created_by} fallback="—" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls page={page} pageSize={pageSize} total={total}
            onPage={setPage} onPageSize={setPageSize}
            pageSizeOptions={[25, 50, 100, 200]} />
        </Card>
      )}
    </div>
  );
}
